import { getNote } from '@/lib/music/notes';
import { ledgerSteps, STAFF_LINE_STEPS, stepToY, TREBLE_CLEF_PATH } from '@/lib/music/staff';
import { DURATIONS } from '@/lib/music/rhythm';
import type { RhythmEvent } from '@/lib/exercise/rhythmEngine';

interface RhythmStaffProps {
  events: RhythmEvent[];
  beatsPerMeasure: number;
  timeSignature?: string | null;
  /** índice do evento em execução */
  currentIndex?: number;
  /** marcação por evento após avaliação */
  marks?: Record<number, 'correct' | 'wrong' | 'warn'>;
  /** a região da pausa acende durante todo o silêncio (aulas de pausa) */
  restGlow?: boolean;
  space?: number;
  className?: string;
}

const MARK_FILL: Record<'correct' | 'wrong' | 'warn', string> = {
  correct: 'fill-accent-500',
  wrong: 'fill-secondary-600',
  warn: 'fill-primary-500',
};

const MARK_STROKE: Record<'correct' | 'wrong' | 'warn', string> = {
  correct: 'stroke-accent-500',
  wrong: 'stroke-secondary-600',
  warn: 'stroke-primary-500',
};

/**
 * Pauta com figuras rítmicas completas (semibreve, mínima, semínima, colcheia,
 * semicolcheia e pausas), barras de compasso e destaque do evento atual.
 */
export default function RhythmStaff({
  events,
  beatsPerMeasure,
  timeSignature = null,
  currentIndex = -1,
  marks = {},
  restGlow = false,
  space = 24,
  className = '',
}: RhythmStaffProps) {
  const gLineY = space * 5.0;
  const height = space * 10.2;
  const leftPad = space * 0.6;
  const clefX = leftPad + space * 1.5;

  let cursor = clefX + space * 3.2;
  let tsX: number | null = null;
  if (timeSignature) {
    tsX = clefX + space * 2.4;
    cursor = tsX + space * 2.0;
  }

  const geometry = { gLineY, space };
  const startX = cursor;

  const items: { event: RhythmEvent; index: number; x: number }[] = [];
  const barlines: number[] = [];
  let beatsAcc = 0;
  let x = startX;

  events.forEach((event, index) => {
    if (beatsAcc > 0 && Math.abs((beatsAcc % beatsPerMeasure) - 0) < 1e-6) {
      barlines.push(x - space * 0.55);
    }
    items.push({ event, index, x });
    beatsAcc += event.beats;
    x += space * (2.2 + Math.sqrt(event.beats) * 1.7);
  });

  const endX = x;
  const width = endX + space * 1.0;

  const renderFlags = (stemX: number, stemEndY: number, stemDown: boolean, flags: number) => {
    const dir = stemDown ? -1 : 1;
    const paths = [];
    for (let f = 0; f < flags; f += 1) {
      const baseY = stemEndY + dir * space * 0.5 * f;
      const d = stemDown
        ? `M ${stemX} ${baseY} C ${stemX - space * 0.85} ${baseY - space * 0.15} ${
            stemX - space * 1.0
          } ${baseY - space * 0.95} ${stemX - space * 0.28} ${baseY - space * 1.45}`
        : `M ${stemX} ${baseY} C ${stemX + space * 0.85} ${baseY + space * 0.15} ${
            stemX + space * 1.0
          } ${baseY + space * 0.95} ${stemX + space * 0.28} ${baseY + space * 1.45}`;
      paths.push(d);
    }
    return paths;
  };

  const renderRest = (px: number, id: string) => {
    const lineY = (step: number) => stepToY(step, geometry);
    if (id === 'whole') {
      return (
        <rect
          x={px - space * 0.55}
          y={lineY(2) + space * 0.15}
          width={space * 1.1}
          height={space * 0.5}
          className="fill-foreground-950"
        />
      );
    }
    if (id === 'half') {
      return (
        <rect
          x={px - space * 0.55}
          y={lineY(1) - space * 0.6}
          width={space * 1.1}
          height={space * 0.5}
          className="fill-foreground-950"
        />
      );
    }
    const centerY = gLineY;
    if (id === 'quarter') {
      return (
        <path
          d={`M ${px + space * 0.25} ${centerY - space * 1.7} C ${px - space * 0.85} ${
            centerY - space * 1.0
          } ${px + space * 0.95} ${centerY - space * 0.35} ${px - space * 0.1} ${
            centerY + space * 0.3
          } C ${px - space * 0.95} ${centerY + space * 0.85} ${px + space * 0.85} ${
            centerY + space * 1.15
          } ${px + space * 0.15} ${centerY + space * 1.75}`}
          fill="none"
          className="stroke-foreground-950"
          strokeWidth={space * 0.14}
          strokeLinecap="round"
        />
      );
    }
    const dots = id === 'sixteenth' ? 2 : 1;
    return (
      <g>
        <line
          x1={px - space * 0.35}
          y1={centerY - space * 1.5}
          x2={px + space * 0.35}
          y2={centerY + space * 0.35}
          className="stroke-foreground-950"
          strokeWidth={space * 0.13}
          strokeLinecap="round"
        />
        {Array.from({ length: dots }).map((_, i) => (
          <circle
            key={`rest-dot-${i}`}
            cx={px + space * 0.35 - i * space * 0.5}
            cy={centerY + space * 0.35 - i * space * 0.55}
            r={space * 0.17}
            className="fill-foreground-950"
          />
        ))}
      </g>
    );
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={`w-full h-auto ${className}`}
      role="img"
      aria-label="Pauta rítmica na clave de sol"
    >
      {STAFF_LINE_STEPS.map((step) => (
        <line
          key={`line-${step}`}
          x1={0}
          x2={width}
          y1={stepToY(step, geometry)}
          y2={stepToY(step, geometry)}
          className="stroke-foreground-900"
          strokeWidth={Math.max(1.1, space * 0.07)}
        />
      ))}

      <g transform={`translate(${clefX} ${gLineY}) scale(${space})`}>
        <path
          d={TREBLE_CLEF_PATH}
          fill="none"
          className="stroke-foreground-950"
          strokeWidth={0.22}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {tsX !== null && timeSignature ? (
        <g className="fill-foreground-950 font-heading" textAnchor="middle">
          <text x={tsX} y={gLineY - space * 0.4} fontSize={space * 1.6} fontWeight={700}>
            {timeSignature.split('/')[0]}
          </text>
          <text x={tsX} y={gLineY + space * 1.5} fontSize={space * 1.6} fontWeight={700}>
            {timeSignature.split('/')[1]}
          </text>
        </g>
      ) : null}

      {barlines.map((bx) => (
        <line
          key={`bar-${bx}`}
          x1={bx}
          x2={bx}
          y1={stepToY(3, geometry)}
          y2={stepToY(-1, geometry)}
          className="stroke-foreground-900"
          strokeWidth={Math.max(1.1, space * 0.07)}
        />
      ))}

      <line
        x1={endX - space * 0.2}
        x2={endX - space * 0.2}
        y1={stepToY(3, geometry)}
        y2={stepToY(-1, geometry)}
        className="stroke-foreground-950"
        strokeWidth={space * 0.16}
      />
      <line
        x1={endX - space * 0.45}
        x2={endX - space * 0.45}
        y1={stepToY(3, geometry)}
        y2={stepToY(-1, geometry)}
        className="stroke-foreground-950"
        strokeWidth={space * 0.07}
      />

      {items.map(({ event, index, x: px }) => {
        const isCurrent = index === currentIndex;
        const mark = marks[index];
        const highlight = (
          <rect
            key={`hl-${index}`}
            x={px - space * 1.15}
            y={space * 0.7}
            width={space * 2.1}
            height={space * 7.8}
            rx={space * 0.4}
            className={isCurrent ? 'fill-primary-100' : 'fill-transparent'}
          />
        );

        if (event.kind === 'rest') {
          const glowWidth = space * (1.3 + Math.sqrt(event.beats) * 1.9);
          return (
            <g key={`ev-${index}`}>
              {highlight}
              {restGlow && isCurrent ? (
                <rect
                  className="rest-glow-target animate-rest-glow fill-accent-200"
                  x={px - glowWidth / 2}
                  y={space * 0.55}
                  width={glowWidth}
                  height={space * 8.1}
                  rx={space * 0.8}
                />
              ) : null}
              <g opacity={mark === 'wrong' ? 0.55 : 1}>{renderRest(px, event.durationId)}</g>
            </g>
          );
        }

        const note = getNote(event.noteId);
        const def = DURATIONS[event.durationId];
        const y = stepToY(note.step, geometry);
        const rx = space * 0.62;
        const ry = space * 0.46;
        const stemDown = note.step >= 1;
        const stemX = stemDown ? px - rx * 0.92 : px + rx * 0.92;
        const stemLen = space * 3.3;
        const stemEndY = stemDown ? y + stemLen : y - stemLen;
        const headFill = mark ? MARK_FILL[mark] : isCurrent ? 'fill-primary-600' : 'fill-foreground-950';
        const headStroke = mark
          ? MARK_STROKE[mark]
          : isCurrent
            ? 'stroke-primary-600'
            : 'stroke-foreground-950';

        return (
          <g key={`ev-${index}`}>
            {highlight}
            {ledgerSteps(note.step).map((lstep) => (
              <line
                key={`led-${index}-${lstep}`}
                x1={px - space * 1.0}
                x2={px + space * 1.0}
                y1={stepToY(lstep, geometry)}
                y2={stepToY(lstep, geometry)}
                className="stroke-foreground-950"
                strokeWidth={Math.max(1.3, space * 0.08)}
              />
            ))}
            {def.stem ? (
              <line
                x1={stemX}
                x2={stemX}
                y1={y}
                y2={stemEndY}
                className="stroke-foreground-950"
                strokeWidth={Math.max(1.4, space * 0.085)}
              />
            ) : null}
            {def.flags > 0
              ? renderFlags(stemX, stemEndY, stemDown, def.flags).map((d, fi) => (
                  <path
                    key={`flag-${index}-${fi}`}
                    d={d}
                    fill="none"
                    className="stroke-foreground-950"
                    strokeWidth={Math.max(1.4, space * 0.11)}
                    strokeLinecap="round"
                  />
                ))
              : null}
            <ellipse
              cx={px}
              cy={y}
              rx={rx}
              ry={ry}
              transform={`rotate(-18 ${px} ${y})`}
              className={def.filled ? headFill : headStroke}
              fill={def.filled ? undefined : 'none'}
              stroke={def.filled ? 'none' : undefined}
              strokeWidth={def.filled ? 0 : Math.max(1.4, space * 0.085)}
            />
            {def.dots ? (
              <circle
                cx={px + rx + space * 0.55}
                cy={y}
                r={space * 0.16}
                className="fill-foreground-950"
              />
            ) : null}
            {(() => {
              const nextItem = items[index + 1];
              if (
                event.tied ||
                !nextItem ||
                nextItem.event.kind !== 'note' ||
                nextItem.event.tied !== true
              ) {
                return null;
              }
              const nextNote = getNote(nextItem.event.noteId);
              const nextY = stepToY(nextNote.step, geometry);
              const arcDir = stemDown ? -1 : 1;
              const fromX = px + rx * 1.05;
              const toX = nextItem.x - rx * 1.05;
              const midY = (y + nextY) / 2 + arcDir * space * 0.95;
              return (
                <path
                  d={`M ${fromX} ${y + arcDir * space * 0.2} Q ${
                    (fromX + toX) / 2
                  } ${midY} ${toX} ${nextY + arcDir * space * 0.2}`}
                  fill="none"
                  className="stroke-foreground-950"
                  strokeWidth={Math.max(1.3, space * 0.09)}
                  strokeLinecap="round"
                />
              );
            })()}
          </g>
        );
      })}
    </svg>
  );
}